import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { Container } from 'reactstrap';
import { useMediaQuery } from 'usehooks-ts';
import { debounce } from 'lodash';

import AppMeta from 'components/App/Meta';
import AppBreadcrumb from 'components/App/Breadcrumb';
import MessageList from 'components/MessageList';
import Chat from 'components/Chat';
import BookingInfo from 'components/BookingInfo';

import cn from 'utils/classnames';
import { connectSocket, socketEmit } from 'utils/socket';

import INITIAL_VALUE from 'constants/initialValue';
import { storageKey, LOGIN_TYPE } from 'constants/index';

import noMessageIcon from 'assets/images/no-message.svg';

import styles from './MessagesWithCustomers.module.scss';

const CHAT_ROOMS_LIMIT = 10;
const CHAT_ROOMS_PAGE = 1;
const CHAT_MESSAGES_LIMIT = 10;
const CHAT_MESSAGES_PAGE = 1;

const formatMessageTime = (createdAt) => {
  if (!createdAt) {
    return '';
  }

  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

const parseFileAttachmentFromText = (text) => {
  if (!text || typeof text !== 'string') {
    return null;
  }

  const trimmedText = text.trim();

  if (!trimmedText.startsWith('{')) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmedText);

    if (parsed?.url && parsed?.name) {
      return {
        isImage: false,
        url: parsed.url,
        name: parsed.name,
        sizeLabel: parsed.size || '',
        typeLabel: parsed.type || 'FILE',
      };
    }
  } catch {
    return null;
  }

  return null;
};

const mapChatMessage = (message) => {
  const mappedMessage = {
    id: message._id,
    type: message.user ? 'incoming' : 'outgoing',
    content: '',
    attachments: [],
    time: formatMessageTime(message.createdAt),
    createdAt: message.createdAt || '',
  };

  if (message.image) {
    mappedMessage.attachments.push({
      id: `${message._id}-image`,
      isImage: true,
      url: message.image,
      name: 'Ảnh đính kèm',
    });

    return mappedMessage;
  }

  const fileAttachment = parseFileAttachmentFromText(message.text);

  if (fileAttachment) {
    mappedMessage.attachments.push({
      id: `${message._id}-file`,
      ...fileAttachment,
    });

    return mappedMessage;
  }

  mappedMessage.content = message.text || '';

  return mappedMessage;
};

const mapChatMessages = (messages = []) => messages.map(mapChatMessage);

const getChatRoomPlaceId = (chatRoom) => {
  const place = chatRoom?.lastMessage?.place;

  if (!place) {
    return '';
  }

  if (typeof place === 'object') {
    return place._id || '';
  }

  return String(place);
};

const breadcrumbList = [
  {
    name: 'Hộp thư',
    isActive: false,
    link: '',
  },
  {
    name: 'Tin nhắn với khách hàng',
    isActive: true,
    link: '',
  },
];

function MessagesWithCustomers(props) {
  const {
    isLoadingChatRooms,
    chatRooms,
    chatRoomsTotal,
    isLoadingChatRoomDetail,
    chatRoomDetail,
    isLoadingChatMessages,
    chatMessages,
    chatMessagesTotal,
    isSendingChatMessage,
    sentMessageClientIds,
    actionGetListChatRoom,
    actionResetListChatRoom,
    actionGetChatRoomDetail,
    actionResetChatRoomDetail,
    actionGetChatRoomMessages,
    actionResetChatRoomMessages,
    actionSendChatMessage,
    actionReceiveChatRoomMessage,
    actionReceiveNewChatRoom,
    actionReadChatRoom,
  } = props;

  const location = useLocation();
  const history = useHistory();

  const placeId = React.useMemo(
    () => new URLSearchParams(location.search).get('place') || '',
    [location.search],
  );

  const roomIdFromUrl = React.useMemo(
    () => new URLSearchParams(location.search).get('room') || '',
    [location.search],
  );

  const [selectedId, setSelectedId] = React.useState(
    () => new URLSearchParams(window.location.search).get('room') || null,
  );
  const [keyword, setKeyword] = React.useState('');
  const [isUnreadOnly, setIsUnreadOnly] = React.useState(false);
  const [isDetailOpen, setIsDetailOpen] = React.useState(false);
  const [isBookingPanelOpen, setIsBookingPanelOpen] = React.useState(false);
  const isCompactView = useMediaQuery('(max-width: 1259px)');
  const isNarrowDetailView = useMediaQuery('(max-width: 820px)');

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const fetchChatRooms = React.useCallback((
    searchKeyword,
    page = CHAT_ROOMS_PAGE,
    isLoadMore = false,
  ) => {
    if (!placeId) {
      actionResetListChatRoom();
      return;
    }

    const trimmedKeyword = searchKeyword.trim();

    actionGetListChatRoom({
      place: placeId,
      limit: CHAT_ROOMS_LIMIT,
      page,
      ...(trimmedKeyword ? { q: trimmedKeyword } : {}),
      ...(isUnreadOnly ? { unread: true } : {}),
      isLoadMore,
    });
  }, [placeId, isUnreadOnly, actionGetListChatRoom, actionResetListChatRoom]);

  const handleLoadMore = React.useCallback(() => {
    if (isLoadingChatRooms || chatRooms.length >= chatRoomsTotal) {
      return;
    }

    const nextPage = Math.floor(chatRooms.length / CHAT_ROOMS_LIMIT) + 1;
    fetchChatRooms(keyword, nextPage, true);
  }, [isLoadingChatRooms, chatRooms.length, chatRoomsTotal, keyword, fetchChatRooms]);

  const debouncedSearchChatRooms = React.useMemo(
    () => debounce((searchKeyword) => fetchChatRooms(searchKeyword), 400),
    [fetchChatRooms],
  );

  const handleSearch = React.useCallback((value) => {
    setKeyword(value);
    debouncedSearchChatRooms(value);
  }, [debouncedSearchChatRooms]);

  const updateRoomInUrl = React.useCallback((chatRoomId) => {
    const params = new URLSearchParams(location.search);

    if (chatRoomId) {
      params.set('room', chatRoomId);
    } else {
      params.delete('room');
    }

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [history, location.pathname, location.search]);

  React.useEffect(() => {
    debouncedSearchChatRooms.cancel();
    fetchChatRooms(keyword);
  }, [placeId, isUnreadOnly]);

  React.useEffect(
    () => () => debouncedSearchChatRooms.cancel(),
    [debouncedSearchChatRooms],
  );

  React.useEffect(() => () => {
    actionResetListChatRoom();
    actionResetChatRoomDetail();
    actionResetChatRoomMessages();
  }, [actionResetListChatRoom, actionResetChatRoomDetail, actionResetChatRoomMessages]);

  React.useEffect(() => {
    setSelectedId((prevSelectedId) => {
      if (roomIdFromUrl) {
        return roomIdFromUrl;
      }

      if (prevSelectedId && chatRooms.some((room) => room._id === prevSelectedId)) {
        return prevSelectedId;
      }

      return chatRooms[0]?._id ?? null;
    });
  }, [chatRooms, roomIdFromUrl]);

  React.useEffect(() => {
    if (!selectedId || roomIdFromUrl === selectedId) {
      return;
    }

    if (isCompactView && !isDetailOpen) {
      return;
    }

    updateRoomInUrl(selectedId);
  }, [selectedId, roomIdFromUrl, isCompactView, isDetailOpen, updateRoomInUrl]);

  React.useEffect(() => {
    if (!roomIdFromUrl || !isCompactView) {
      return;
    }

    if (chatRooms.some((room) => room._id === roomIdFromUrl)) {
      setIsDetailOpen(true);
    }
  }, [roomIdFromUrl, isCompactView, chatRooms]);

  React.useEffect(() => {
    if (!selectedId || !placeId) {
      actionResetChatRoomDetail();
      return;
    }

    actionGetChatRoomDetail({ id: selectedId, place: placeId });
  }, [selectedId, placeId, actionGetChatRoomDetail, actionResetChatRoomDetail]);

  React.useEffect(() => {
    if (!selectedId || !placeId) {
      actionResetChatRoomMessages();
      return;
    }

    actionGetChatRoomMessages({
      id: selectedId,
      place: placeId,
      limit: CHAT_MESSAGES_LIMIT,
      page: CHAT_MESSAGES_PAGE,
    });
  }, [selectedId, placeId, actionGetChatRoomMessages, actionResetChatRoomMessages]);

  const activeRoomId = selectedId || roomIdFromUrl;

  React.useEffect(() => {
    if (!placeId) {
      return undefined;
    }

    const socket = connectSocket();

    const joinPlaceChannel = () => {
      socketEmit('joinChannel', `place:${placeId}`);
    };

    const handleChatRoomListUpdate = (chatRoom) => {
      if (!chatRoom?._id) {
        return;
      }

      const roomPlaceId = getChatRoomPlaceId(chatRoom);

      if (roomPlaceId && roomPlaceId !== placeId) {
        return;
      }

      if (activeRoomId === chatRoom._id && placeId) {
        actionReadChatRoom({ id: chatRoom._id, place: placeId });
      }

      actionReceiveNewChatRoom(chatRoom);
    };

    const handleChatRoomUpdate = (chatRoom) => {
      handleChatRoomListUpdate(chatRoom);

      if (chatRoom?._id === activeRoomId && placeId) {
        actionGetChatRoomDetail({ id: chatRoom._id, place: placeId });
      }
    };

    socket.on('connect', joinPlaceChannel);
    socket.on('new_chat_room', handleChatRoomListUpdate);
    socket.on('list_room_new_message', handleChatRoomListUpdate);
    socket.on('update_chat_room', handleChatRoomUpdate);

    if (socket.connected) {
      joinPlaceChannel();
    }

    return () => {
      socket.off('connect', joinPlaceChannel);
      socket.off('new_chat_room', handleChatRoomListUpdate);
      socket.off('list_room_new_message', handleChatRoomListUpdate);
      socket.off('update_chat_room', handleChatRoomUpdate);
    };
  }, [
    placeId,
    activeRoomId,
    actionReceiveNewChatRoom,
    actionReadChatRoom,
    actionGetChatRoomDetail,
  ]);

  React.useEffect(() => {
    if (!activeRoomId) {
      return undefined;
    }

    const socket = connectSocket();
    const roomChannel = `room:${activeRoomId}`;

    const joinChannel = () => {
      socketEmit('joinChannel', roomChannel);
    };

    const handleRoomNewMessage = (payload) => {
      const message = payload;

      if (!message?._id) {
        return;
      }

      actionReceiveChatRoomMessage(message);
    };

    socket.on('connect', joinChannel);
    socket.on('room_new_message', handleRoomNewMessage);

    if (socket.connected) {
      joinChannel();
    }

    return () => {
      socketEmit('leaveChannel', roomChannel);
      socket.off('connect', joinChannel);
      socket.off('room_new_message', handleRoomNewMessage);
    };
  }, [activeRoomId, actionReceiveChatRoomMessage]);

  const handleLoadMoreMessages = React.useCallback(() => {
    if (
      !selectedId
      || !placeId
      || isLoadingChatMessages
      || chatMessages.length >= chatMessagesTotal
    ) {
      return;
    }

    const nextPage = Math.floor(chatMessages.length / CHAT_MESSAGES_LIMIT) + 1;

    actionGetChatRoomMessages({
      id: selectedId,
      place: placeId,
      limit: CHAT_MESSAGES_LIMIT,
      page: nextPage,
      isLoadMore: true,
    });
  }, [
    selectedId,
    placeId,
    isLoadingChatMessages,
    chatMessages.length,
    chatMessagesTotal,
    actionGetChatRoomMessages,
  ]);

  const mappedChatMessages = React.useMemo(
    () => mapChatMessages(chatMessages),
    [chatMessages],
  );

  const handleSendMessage = React.useCallback((payload) => {
    if (!selectedId || !placeId) {
      return;
    }

    actionSendChatMessage({
      id: selectedId,
      place: placeId,
      text: payload.text,
      textClientId: payload.textClientId,
      files: payload.files,
    });
  }, [selectedId, placeId, actionSendChatMessage]);

  React.useEffect(() => {
    if (!isCompactView) {
      setIsDetailOpen(false);
    }
  }, [isCompactView]);

  React.useEffect(() => {
    if (!isNarrowDetailView) {
      setIsBookingPanelOpen(false);
    }
  }, [isNarrowDetailView]);

  const handleSelectConversation = React.useCallback((chatRoom) => {
    const chatRoomId = chatRoom._id;

    setSelectedId(chatRoomId);
    updateRoomInUrl(chatRoomId);

    if (isCompactView) {
      setIsDetailOpen(true);
    }

    setIsBookingPanelOpen(false);

    if (chatRoom.isRead === false && placeId) {
      actionReadChatRoom({ id: chatRoomId, place: placeId });
    }
  }, [isCompactView, placeId, updateRoomInUrl, actionReadChatRoom]);

  const handleBackToList = React.useCallback(() => {
    setIsDetailOpen(false);
    setIsBookingPanelOpen(false);
    updateRoomInUrl(null);
  }, [updateRoomInUrl]);

  const handleOpenBookingInfo = React.useCallback(() => {
    setIsBookingPanelOpen(true);
  }, []);

  const handleBackToChat = React.useCallback(() => {
    setIsBookingPanelOpen(false);
  }, []);

  return (
    <div className={`page-content ${styles.Page}`}>
      <AppMeta name="Tin nhắn với khách hàng" />

      <Container fluid>
        <AppBreadcrumb
          pageTitle="TIN NHẮN VỚI KHÁCH HÀNG"
          breadcrumbList={breadcrumbList}
        />
        <div
          className={cn(isExtranet ? styles.ContentExtranet : styles.Content, {
            [styles.ShowDetail]: isCompactView && isDetailOpen,
            [styles.ShowBookingInfo]: isNarrowDetailView && isBookingPanelOpen,
          })}
        >
          <div className={styles.MessageListPane}>
            <MessageList
              className={isCompactView ? styles.MessageListFullWidth : ''}
              listChatRooms={chatRooms}
              selectedId={selectedId}
              keyword={keyword}
              isUnreadOnly={isUnreadOnly}
              onSelect={handleSelectConversation}
              onSearch={handleSearch}
              onToggleUnreadOnly={setIsUnreadOnly}
              onLoadMore={handleLoadMore}
              isLoadingMore={isLoadingChatRooms}
              hasMore={chatRooms.length < chatRoomsTotal}
            />
          </div>
          <div className={styles.DetailPane}>
            {chatRooms.length === 0 && !activeRoomId && !isLoadingChatRooms ? (
              <div className={styles.EmptyState}>
                <img
                  src={noMessageIcon}
                  alt=""
                  className={styles.EmptyStateIcon}
                />
                <p className={styles.EmptyStateTitle}>Không có tin nhắn</p>
                <p className={styles.EmptyStateDescription}>
                  Cuộc trao đổi của Quý vị với Khách hàng về các đặt phòng sẽ được hiển thị ở đây
                </p>
              </div>
            ) : (
              <>
                <div className={styles.ChatPane}>
                  <Chat
                    bookingInfo={chatRoomDetail}
                    selectedId={selectedId}
                    messages={mappedChatMessages}
                    isLoadingMessages={isLoadingChatMessages}
                    hasMoreMessages={chatMessages.length < chatMessagesTotal}
                    onLoadMoreMessages={handleLoadMoreMessages}
                    isVisible={!isCompactView || isDetailOpen}
                    isSendingMessage={isSendingChatMessage}
                    sentClientIds={sentMessageClientIds}
                    onSendMessage={handleSendMessage}
                    onBack={isCompactView ? handleBackToList : undefined}
                    showBookingInfoToggle={isNarrowDetailView && isDetailOpen}
                    onOpenBookingInfo={handleOpenBookingInfo}
                  />
                </div>
                <div className={styles.BookingInfoPane}>
                  <BookingInfo
                    chatRoomDetail={chatRoomDetail}
                    isLoading={isLoadingChatRoomDetail}
                    onBack={isNarrowDetailView ? handleBackToChat : undefined}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
}

MessagesWithCustomers.propTypes = {
  isLoadingChatRooms: PropTypes.bool,
  chatRooms: PropTypes.instanceOf(Array),
  chatRoomsTotal: PropTypes.number,
  isLoadingChatRoomDetail: PropTypes.bool,
  chatRoomDetail: PropTypes.instanceOf(Object),
  isLoadingChatMessages: PropTypes.bool,
  chatMessages: PropTypes.instanceOf(Array),
  chatMessagesTotal: PropTypes.number,
  isSendingChatMessage: PropTypes.bool,
  sentMessageClientIds: PropTypes.instanceOf(Array),
  actionGetListChatRoom: PropTypes.func,
  actionResetListChatRoom: PropTypes.func,
  actionGetChatRoomDetail: PropTypes.func,
  actionResetChatRoomDetail: PropTypes.func,
  actionGetChatRoomMessages: PropTypes.func,
  actionResetChatRoomMessages: PropTypes.func,
  actionSendChatMessage: PropTypes.func,
  actionReceiveChatRoomMessage: PropTypes.func,
  actionReceiveNewChatRoom: PropTypes.func,
  actionReadChatRoom: PropTypes.func,
};

MessagesWithCustomers.defaultProps = {
  isLoadingChatRooms: false,
  chatRooms: [],
  chatRoomsTotal: 0,
  isLoadingChatRoomDetail: false,
  chatRoomDetail: null,
  isLoadingChatMessages: false,
  chatMessages: [],
  chatMessagesTotal: 0,
  isSendingChatMessage: false,
  sentMessageClientIds: [],
  actionGetListChatRoom: INITIAL_VALUE.FUNCTION,
  actionResetListChatRoom: INITIAL_VALUE.FUNCTION,
  actionGetChatRoomDetail: INITIAL_VALUE.FUNCTION,
  actionResetChatRoomDetail: INITIAL_VALUE.FUNCTION,
  actionGetChatRoomMessages: INITIAL_VALUE.FUNCTION,
  actionResetChatRoomMessages: INITIAL_VALUE.FUNCTION,
  actionSendChatMessage: INITIAL_VALUE.FUNCTION,
  actionReceiveChatRoomMessage: INITIAL_VALUE.FUNCTION,
  actionReceiveNewChatRoom: INITIAL_VALUE.FUNCTION,
  actionReadChatRoom: INITIAL_VALUE.FUNCTION,
};

export default MessagesWithCustomers;
